'use client';

import * as React from 'react';
import { LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: string | undefined;
  trendDirection?: 'up' | 'down' | 'neutral' | undefined;
}

/**
 * MetricCard Component
 *
 * Displays a single metric with icon, value, and optional trend indicator.
 * Used within DashboardMetrics to show key performance indicators.
 *
 * @example
 * ```tsx
 * <MetricCard
 *   title="Total Jobs"
 *   value={125}
 *   icon={Briefcase}
 *   trend="+10%"
 *   trendDirection="up"
 * />
 * ```
 */
export function MetricCard({
  title,
  value,
  icon: Icon,
  trend,
  trendDirection = 'neutral',
}: MetricCardProps) {
  const trendColors = {
    up: 'text-success-600',
    down: 'text-error-600',
    neutral: 'text-neutral-500',
  };

  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          {/* Icon */}
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50">
            <Icon className="h-6 w-6 text-primary-600" />
          </div>

          {/* Trend Badge */}
          {trend && (
            <div
              className={cn(
                'rounded-md px-2 py-1 text-sm font-medium',
                trendColors[trendDirection]
              )}
            >
              {trend}
            </div>
          )}
        </div>

        {/* Value */}
        <div className="mt-4">
          <p className="text-3xl font-bold text-text-primary">{value}</p>
        </div>

        {/* Title */}
        <div className="mt-2">
          <p className="text-sm font-medium text-text-secondary">{title}</p>
        </div>
      </CardContent>
    </Card>
  );
}
